import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { of, switchMap, catchError, timeout } from 'rxjs';
import { ProjectService } from '../../../views/projects/project.service';
import { UserService } from 'app/core/user/user.service';

export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    // Prevent recursive guard on /sign-out
    if (state.url === '/sign-out') {
        console.log('[AuthGuard] Allowing /sign-out to prevent recursion');
        return of(true);
    }
    console.log('[AuthGuard] Checking route:', route, 'state:', state);
    const router: Router = inject(Router);
    const authService: AuthService = inject(AuthService);
    const userService = inject(UserService);

    // Prevenir bucles - verificar si ya estamos en sign-in
    if (state.url.includes('/sign-in')) {
        return of(false);
    }

    // Para sign-out, verificar autenticación primero
    if (state.url.includes('/sign-out')) {
        return authService.check().pipe(
            switchMap((authenticated) => {
                if (!authenticated) {
                    // Si no está autenticado, redirigir a login
                    router.navigate(['/sign-in']);
                    return of(false);
                }
                // Si está autenticado, permitir acceso a sign-out
                return of(true);
            })
        );
    }

    let namespace = route.data?.namespace || null;
    let hasRequiredRole = true; // Cambiar lógica: true = tiene acceso

    // Solo verificar roles si hay datos de usuario
    if (userService.currentUser) {
        switch (namespace) {
            case 'project': {
                const projectService: ProjectService = inject(ProjectService);
                // Admin siempre tiene acceso
                if (userService.currentUser.role === 'admin') {
                    hasRequiredRole = true;
                } else if (route.data?.expectedRole) {
                    hasRequiredRole = projectService.hasRole(route.data.expectedRole);
                }
                break;
            }
            default: {
                // Admin siempre tiene acceso
                if (userService.currentUser.role === 'admin') {
                    hasRequiredRole = true;
                } else if (route.data?.expectedRole) {
                    hasRequiredRole = authService.hasRole(route.data.expectedRole);
                }
            }
        }
    } else if (route.data?.expectedRole) {
        // Si se requiere un rol específico pero no hay usuario, denegar acceso
        hasRequiredRole = false;
    }
    // Check the authentication status con timeout
    return authService.check().pipe(
        timeout(10000), // Aumentar timeout a 10 segundos
        switchMap((authenticated) => {

            // Si no está autenticado O no tiene el rol requerido
            if (!authenticated || !hasRequiredRole) {
                console.warn('AuthGuard: Access denied', {
                    authenticated,
                    hasRequiredRole,
                    url: state.url,
                    expectedRole: route.data?.expectedRole
                });

                // Si no está autenticado, redirigir a login
                if (!authenticated) {
                    router.navigate(['/sign-in']);
                } else {
                    // Si está autenticado pero no tiene rol, redirigir a dashboard
                    router.navigate(['/dashboard']);
                }
                return of(false);
            }
            // Permitir acceso
            return of(true);
        }),
        catchError((error) => {
            console.error('Auth Guard error:', error);
            // En caso de error, también redirigir a login
            router.navigate(['/sign-in']);
            return of(false);
        })
    );
};
